/**
 * Service for persisting table selections across page reloads and pagination using localStorage.
 *
 * Features:
 * - Persists checkbox selections across page refreshes and pagination
 * - Supports multiple tables on the same page through unique table identifiers
 * - Cross-tab synchronization via storage events
 * - Automatic cleanup of expired selections after 24 hours
 * - Stores checkbox values (not row IDs) as stable identifiers
 *
 * Usage:
 * Tables must have:
 * - A unique table_identifier attribute
 * - enable_selection_persistence set to true
 * - Checkboxes with stable values that persist across page loads
 *
 * @example
 * // In Ruby view component
 * DataTableBuilder.new(scope: entities, table_identifier: 'entities_table',
 *                      enable_selection_persistence: true)
 */

// TypeScript interfaces for type safety
interface SelectionMetadata {
  timestamp: number;
  count: number;
}

interface StorageEventDetail {
  selectedIds: string[];
}

interface SelectionPersistenceConfig {
  STORAGE_PREFIX: string;
  STORAGE_SEPARATOR: string;
  TTL_HOURS: number;
  MAX_SELECTION_SIZE: number;
}

export class SelectionPersistenceService {
  // Configuration constants
  private static readonly CONFIG: SelectionPersistenceConfig = {
    STORAGE_PREFIX: "dataTable",
    STORAGE_SEPARATOR: ":",
    TTL_HOURS: 24, // Selections expire after 24 hours
    MAX_SELECTION_SIZE: 1000, // Maximum number of selections per table
  };

  // Legacy getters for backward compatibility
  private static get STORAGE_PREFIX() {
    return this.CONFIG.STORAGE_PREFIX;
  }
  private static get STORAGE_SEPARATOR() {
    return this.CONFIG.STORAGE_SEPARATOR;
  }
  private static get TTL_HOURS() {
    return this.CONFIG.TTL_HOURS;
  }
  private static get MAX_SELECTION_SIZE() {
    return this.CONFIG.MAX_SELECTION_SIZE;
  }

  private static quotaExceededCallback:
    | ((tableId: string, error: Error) => void)
    | null = null;

  /**
   * Generate a storage key for a specific table
   */
  private static getStorageKey(tableId: string): string {
    return `${this.STORAGE_PREFIX}${this.STORAGE_SEPARATOR}${tableId}${this.STORAGE_SEPARATOR}selections`;
  }

  /**
   * Generate a metadata storage key for a specific table
   */
  private static getMetadataKey(tableId: string): string {
    return `${this.STORAGE_PREFIX}${this.STORAGE_SEPARATOR}${tableId}${this.STORAGE_SEPARATOR}metadata`;
  }

  /**
   * Save selections for a specific table with validation and error handling
   */
  static saveSelections(tableId: string, selectedIds: string[]): boolean {
    if (!tableId) {
      return false;
    }

    // Validate input
    if (!this.validateSelections(selectedIds)) {
      console.warn(
        `Invalid selections for table ${tableId}: Too many selections or invalid IDs`,
      );
      return false;
    }

    try {
      const key = this.getStorageKey(tableId);
      const metadataKey = this.getMetadataKey(tableId);

      if (selectedIds.length === 0) {
        // Clear storage when no selections
        localStorage.removeItem(key);
        localStorage.removeItem(metadataKey);
      } else {
        // Save selections with metadata
        const metadata: SelectionMetadata = {
          timestamp: Date.now(),
          count: selectedIds.length,
        };
        localStorage.setItem(key, JSON.stringify(selectedIds));
        localStorage.setItem(metadataKey, JSON.stringify(metadata));
      }
      return true;
    } catch (error) {
      this.handleStorageError(tableId, error as Error);
      return false;
    }
  }

  /**
   * Load selections for a specific table
   */
  static loadSelections(tableId: string): string[] {
    if (!tableId) {
      return [];
    }

    try {
      const key = this.getStorageKey(tableId);
      const metadataKey = this.getMetadataKey(tableId);

      const selectionsJson = localStorage.getItem(key);
      const metadataJson = localStorage.getItem(metadataKey);

      if (!selectionsJson) {
        return [];
      }

      // Check if selections have expired
      if (metadataJson) {
        const metadata: SelectionMetadata = JSON.parse(metadataJson);
        const ageInHours = (Date.now() - metadata.timestamp) / (1000 * 60 * 60);

        if (ageInHours > this.TTL_HOURS) {
          // Expired, clean up
          this.clearSelections(tableId);
          return [];
        }
      }

      const selections = JSON.parse(selectionsJson);
      return selections;
    } catch (error) {
      console.warn(`Failed to load selections for table ${tableId}:`, error);
      return [];
    }
  }

  /**
   * Clear selections for a specific table
   */
  static clearSelections(tableId: string): void {
    if (!tableId) return;

    try {
      const key = this.getStorageKey(tableId);
      const metadataKey = this.getMetadataKey(tableId);
      localStorage.removeItem(key);
      localStorage.removeItem(metadataKey);
    } catch (error) {
      console.warn(`Failed to clear selections for table ${tableId}:`, error);
    }
  }

  /**
   * Get count of persisted selections for a table (including items not on current page)
   */
  static getPersistedCount(tableId: string): number {
    if (!tableId) return 0;

    try {
      const metadataKey = this.getMetadataKey(tableId);
      const metadataJson = localStorage.getItem(metadataKey);

      if (metadataJson) {
        const metadata: SelectionMetadata = JSON.parse(metadataJson);
        return metadata.count || 0;
      }

      // Fallback to counting selections
      const selections = this.loadSelections(tableId);
      return selections.length;
    } catch (error) {
      console.warn(
        `Failed to get persisted count for table ${tableId}:`,
        error,
      );
      return 0;
    }
  }

  /**
   * Add a single selection to existing persisted selections
   * Uses Set for O(1) lookups instead of O(n) array operations
   */
  static addSelection(tableId: string, itemId: string): void {
    if (!tableId || !itemId) return;

    try {
      const selections = this.loadSelections(tableId);
      const selectionSet = new Set(selections);

      if (!selectionSet.has(itemId)) {
        selectionSet.add(itemId);
        this.saveSelections(tableId, Array.from(selectionSet));
      }
    } catch (error) {
      console.warn(`Failed to add selection for table ${tableId}:`, error);
    }
  }

  /**
   * Remove a single selection from persisted selections
   * Uses Set for O(1) deletions instead of O(n) array operations
   */
  static removeSelection(tableId: string, itemId: string): void {
    if (!tableId || !itemId) return;

    try {
      const selections = this.loadSelections(tableId);
      const selectionSet = new Set(selections);

      if (selectionSet.has(itemId)) {
        selectionSet.delete(itemId);
        this.saveSelections(tableId, Array.from(selectionSet));
      }
    } catch (error) {
      console.warn(`Failed to remove selection for table ${tableId}:`, error);
    }
  }

  /**
   * Check if a specific item is in persisted selections
   */
  static isSelected(tableId: string, itemId: string): boolean {
    if (!tableId || !itemId) return false;

    const selections = this.loadSelections(tableId);
    return selections.includes(itemId);
  }

  /**
   * Validate selections before saving
   * Includes XSS prevention and format validation
   */
  private static validateSelections(selectedIds: string[]): boolean {
    // Check size limit
    if (selectedIds.length > this.MAX_SELECTION_SIZE) {
      return false;
    }

    // Validate each ID is a reasonable string
    return selectedIds.every((id) => {
      if (typeof id !== "string" || id.length === 0 || id.length > 200) {
        return false;
      }

      // Prevent XSS: No HTML/script-like content
      // Check for common XSS patterns
      const xssPatterns = [
        /<script/i,
        /<iframe/i,
        /javascript:/i,
        /on[a-z]{1,20}\s{0,10}=/i, // Event handlers like onclick=
        /<\s*\w+/, // Any HTML tags
      ];

      if (xssPatterns.some((pattern) => pattern.test(id))) {
        console.warn("Potential XSS attempt detected in selection ID:", id);
        return false;
      }

      // No null bytes, control characters, or newlines
      if (/[\0\x00-\x1F\x7F\n\r]/.test(id)) {
        return false;
      }

      // Validate format: alphanumeric, hyphens, underscores, dots only
      // This is a reasonable restriction for IDs
      if (!/^[a-zA-Z0-9_\-\.]+$/.test(id)) {
        console.warn(
          "Invalid ID format (must be alphanumeric with -_. only):",
          id,
        );
        return false;
      }

      return true;
    });
  }

  /**
   * Handle storage errors with fallback strategies
   */
  private static handleStorageError(tableId: string, error: Error): void {
    const errorMessage = error.message.toLowerCase();
    const isQuotaError =
      errorMessage.includes("quota") ||
      errorMessage.includes("exceeded") ||
      error.name === "QuotaExceededError";

    if (isQuotaError) {
      console.error(`localStorage quota exceeded for table ${tableId}`);
      // Try to clean up old data
      this.cleanupExpiredSelections();

      // Notify callback if registered
      if (this.quotaExceededCallback) {
        this.quotaExceededCallback(tableId, error);
      }
    } else {
      console.warn(`Failed to save selections for table ${tableId}:`, error);
    }
  }

  /**
   * Register a callback for when localStorage quota is exceeded
   */
  static onQuotaExceeded(
    callback: (tableId: string, error: Error) => void,
  ): void {
    this.quotaExceededCallback = callback;
  }

  /**
   * Clean up expired selections across all tables
   */
  static cleanupExpiredSelections(): void {
    try {
      const keys = Object.keys(localStorage);
      const prefix = this.STORAGE_PREFIX + this.STORAGE_SEPARATOR;

      keys.forEach((key) => {
        if (key.startsWith(prefix) && key.endsWith(":metadata")) {
          try {
            const metadata = JSON.parse(localStorage.getItem(key) || "{}");
            const ageInHours =
              (Date.now() - (metadata.timestamp || 0)) / (1000 * 60 * 60);

            if (ageInHours > this.TTL_HOURS) {
              // Remove both metadata and selections
              localStorage.removeItem(key);
              localStorage.removeItem(key.replace(":metadata", ":selections"));
            }
          } catch (e) {
            // Invalid metadata, remove it
            localStorage.removeItem(key);
          }
        }
      });
    } catch (error) {
      console.warn("Failed to cleanup expired selections:", error);
    }
  }

  /**
   * Listen for storage events to sync selections across tabs
   */
  static addStorageListener(
    tableId: string,
    callback: (selectedIds: string[]) => void,
  ): () => void {
    const key = this.getStorageKey(tableId);

    const handler = (event: StorageEvent) => {
      if (event.key === key && event.newValue !== event.oldValue) {
        try {
          const selectedIds = event.newValue ? JSON.parse(event.newValue) : [];
          callback(selectedIds);
        } catch (error) {
          console.warn("Failed to parse storage event:", error);
        }
      }
    };

    window.addEventListener("storage", handler);

    // Return cleanup function
    return () => window.removeEventListener("storage", handler);
  }
}

// Clean up expired selections on load
if (typeof window !== "undefined") {
  SelectionPersistenceService.cleanupExpiredSelections();
}

export default SelectionPersistenceService;
